import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  DollarSign, 
  Star, 
  Users, 
  TrendingUp, 
  Filter,
  Search,
  Heart,
  Share2,
  Download,
  Eye,
  GitFork,
  Crown,
  Zap,
  Clock,
  CheckCircle,
  CreditCard,
  Package,
  Plus,
  Minus,
  Tag
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { supabase, Agent } from '../lib/supabase';
import { SubscriptionTier } from '../lib/supabase';
import { SubscriptionPlanCard } from './SubscriptionPlanCard';
import { StripeCheckout } from './StripeCheckout';

interface MarketplaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MarketplaceModal({ isOpen, onClose }: MarketplaceModalProps) {
  const [activeTab, setActiveTab] = useState<'browse' | 'featured' | 'my-purchases' | 'sell' | 'subscriptions'>('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [priceFilter, setPriceFilter] = useState<'all' | 'free' | 'paid'>('all');
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'price-low' | 'price-high' | 'rating'>('popular');
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState<SubscriptionTier[]>([]);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutItem, setCheckoutItem] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Fetch real agents from database
  useEffect(() => {
    if (isOpen) {
      fetchMarketplaceAgents();
    }
  }, [isOpen]);

  const fetchMarketplaceAgents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('agents')
        .select('*')
        .order('stars', { ascending: false })
        .limit(100);
      
      if (error) {
        throw error;
      }
      
      setAllAgents(data || []);
    } catch (error) {
      console.error('Error fetching marketplace agents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = allAgents;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(agent => 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(agent => agent.category === selectedCategory);
    }

    // Apply price filter
    if (priceFilter === 'free') {
      filtered = filtered.filter(agent => (agent.price || 0) === 0);
    } else if (priceFilter === 'paid') {
      filtered = filtered.filter(agent => (agent.price || 0) > 0);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return (b.downloads || 0) - (a.downloads || 0);
        case 'newest':
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        case 'price-low':
          return (a.price || 0) - (b.price || 0);
        case 'price-high':
          return (b.price || 0) - (a.price || 0);
        default:
          return 0;
      }
    });

    setFilteredAgents(filtered);
  }, [searchQuery, selectedCategory, priceFilter, sortBy, allAgents]);

  useEffect(() => {
    if (isOpen && activeTab === 'subscriptions') {
      fetchSubscriptionTiers();
      if (user) {
        fetchUserSubscription();
      }
    }
  }, [isOpen, activeTab, user]);

  const fetchSubscriptionTiers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true });
      
      if (error) throw error;
      setSubscriptionTiers(data || []);
    } catch (error) {
      console.error('Error fetching subscription tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscription = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription-manager`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get_user_subscription',
            user_id: user.id
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch subscription');
      }

      const data = await response.json();
      if (data.success) {
        setUserSubscription(data.subscription);
      } else {
        throw new Error(data.error || 'Failed to fetch subscription');
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (agent: Agent) => {
    if (!user) {
      alert('Please sign in to purchase agents');
      return;
    }
    
    setCheckoutItem({
      type: 'agent',
      id: agent.id,
      name: agent.name,
      price: agent.price || 0,
      pricingModel: agent.pricing_model
    });
    setShowCheckout(true);
  };

  const handleSubscribe = (tier: SubscriptionTier) => {
    if (!user) {
      alert('Please sign in to subscribe');
      return;
    }
    
    setCheckoutItem({
      type: 'subscription',
      id: tier.id,
      name: tier.name,
      price: tier.price,
      interval: tier.interval,
      agentLimit: tier.agent_limit
    });
    setShowCheckout(true);
  };

  const formatPrice = (agent: Agent) => {
    if (!agent.price || agent.price === 0) return 'Free';
    
    switch (agent.pricing_model) {
      case 'one-time':
        return `$${agent.price}`;
      case 'subscription_eligible':
        return `$${agent.price} or Subscription`;
      default:
        return `$${agent.price}`;
    }
  };

  const categories = ['all', ...new Set(allAgents.map(a => a.category).filter(Boolean))];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-7xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">AI Agent Marketplace</h2>
              <p className="text-sm opacity-90">Discover, buy, and sell AI agents</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            {[
              { id: 'browse', label: 'Browse Agents', icon: Search },
              { id: 'featured', label: 'Featured', icon: Crown },
              { id: 'subscriptions', label: 'Subscription Plans', icon: Package },
              { id: 'my-purchases', label: 'My Purchases', icon: Package },
              { id: 'sell', label: 'Sell Your Agent', icon: DollarSign }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {(activeTab === 'browse' || activeTab === 'featured') && (
            <div className="p-6">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search agents..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>

                <select
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="all">All Prices</option>
                  <option value="free">Free</option>
                  <option value="paid">Paid</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="popular">Most Popular</option>
                  <option value="newest">Newest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Highest Rated</option>
                </select>
              </div>

              {/* Agent Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(activeTab === 'featured' ? filteredAgents.filter(a => a.featured) : filteredAgents).map((agent) => (
                  <div key={agent.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    {/* Agent Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg text-gray-900">{agent.name}</h3>
                          {agent.featured && <Crown className="w-4 h-4 text-yellow-500" />}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">by {agent.creator}</p>
                        <p className="text-sm text-gray-700 line-clamp-2">{agent.description}</p>
                      </div>
                    </div>

                    {/* Rating and Stats */}
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span>{agent.rating}</span>
                        <span>({agent.totalRatings})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Download className="w-4 h-4" />
                        <span>{agent.downloads}</span>
                      </div>
                      {agent.forksAllowed && (
                        <div className="flex items-center gap-1">
                          <GitFork className="w-4 h-4" />
                          <span>Forkable</span>
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {agent.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Price and Actions */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-green-600">{formatPrice(agent)}</div>
                        <div className="text-xs text-gray-500">
                          {agent.pricingModel === 'free' ? 'Free forever' : 
                           agent.pricingModel === 'one-time' ? 'One-time purchase' : 
                           'Subscription eligible'}
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {agent.demoAvailable && (
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                          <Heart className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePurchase(agent)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
                        >
                          <CreditCard className="w-4 h-4" />
                          {agent.price === 0 ? 'Get Free' : 'Buy Now'}
                        </button>
                      </div>
                    </div>

                    {/* Creator Info */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        {agent.price > 0 && <span>Creator gets {agent.revenueShare}%</span>}
                        <span>Updated {new Date(agent.lastUpdated).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredAgents.length === 0 && (
                <div className="text-center py-12">
                  <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No agents found</h3>
                  <p className="text-gray-600">Try adjusting your search criteria or browse all categories.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'subscriptions' && (
            <div className="p-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Subscription Plans</h3>
                <p className="text-gray-600">Choose a plan that fits your needs</p>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
                </div>
              ) : (
                <>
                  {/* Subscription Tiers */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {subscriptionTiers.map((tier) => (
                      <SubscriptionPlanCard 
                        key={tier.id}
                        tier={tier}
                        isActive={userSubscription?.subscription_tier_id === tier.id}
                        onSubscribe={() => handleSubscribe(tier)}
                      />
                    ))}
                  </div>

                  {/* Subscription Benefits */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
                    <h4 className="font-semibold text-green-900 mb-4 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Subscription Benefits
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Package className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-medium text-green-800 mb-1">Access Multiple Agents</h5>
                          <p className="text-sm text-green-700">
                            Subscribe to access multiple premium agents at a discounted price
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <DollarSign className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-medium text-green-800 mb-1">Cost Savings</h5>
                          <p className="text-sm text-green-700">
                            Save up to 70% compared to individual agent purchases
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Zap className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-medium text-green-800 mb-1">Flexible Management</h5>
                          <p className="text-sm text-green-700">
                            Add or remove agents from your subscription at any time
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subscription FAQ */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Frequently Asked Questions</h4>
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-medium text-gray-800 mb-1">How do subscriptions work?</h5>
                        <p className="text-sm text-gray-600">
                          Choose a subscription tier based on how many premium agents you want to access. You can add or remove agents from your subscription at any time, as long as you stay within your tier's agent limit.
                        </p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-800 mb-1">Can I upgrade or downgrade my plan?</h5>
                        <p className="text-sm text-gray-600">
                          Yes, you can upgrade or downgrade your subscription at any time. Changes will take effect at the start of your next billing cycle.
                        </p>
                      </div>
                      <div>
                        <h5 className="font-medium text-gray-800 mb-1">What happens if I cancel my subscription?</h5>
                        <p className="text-sm text-gray-600">
                          You'll continue to have access to your subscribed agents until the end of your current billing period. After that, you'll lose access unless you purchase them individually.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'my-purchases' && (
            <div className="p-6">
              {user ? (
                <div className="space-y-8">
                  {/* Active Subscription */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Subscription</h3>
                    {userSubscription ? (
                      <div className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-lg text-gray-900">{userSubscription.subscription_tiers.name} Plan</h4>
                            <p className="text-gray-600">${userSubscription.subscription_tiers.price}/{userSubscription.subscription_tiers.interval}</p>
                          </div>
                          <div className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                            Active
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600">Agents Used</span>
                            <span className="font-medium">{userSubscription.user_subscription_agents?.length || 0} of {userSubscription.subscription_tiers.agent_limit}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${((userSubscription.user_subscription_agents?.length || 0) / userSubscription.subscription_tiers.agent_limit) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-200 pt-4 mt-4">
                          <h5 className="font-medium text-gray-900 mb-2">Subscribed Agents</h5>
                          {userSubscription.user_subscription_agents?.length > 0 ? (
                            <div className="space-y-3">
                              {userSubscription.user_subscription_agents.map((item: any) => (
                                <div key={item.agent_id} className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                                      <Bot className="w-4 h-4" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-gray-900">{item.agents.name}</div>
                                      <div className="text-xs text-gray-500">{item.agents.description.substring(0, 50)}...</div>
                                    </div>
                                  </div>
                                  <button className="text-red-600 hover:text-red-700">
                                    <Minus className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              No agents added to your subscription yet
                            </div>
                          )}
                          
                          {userSubscription.user_subscription_agents?.length < userSubscription.subscription_tiers.agent_limit && (
                            <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                              <Plus className="w-4 h-4" />
                              Add Agent to Subscription
                            </button>
                          )}
                        </div>
                        
                        <div className="border-t border-gray-200 pt-4 mt-4 flex justify-between">
                          <div>
                            <div className="text-sm text-gray-600">Next billing date</div>
                            <div className="font-medium">{new Date(userSubscription.current_period_end).toLocaleDateString()}</div>
                          </div>
                          <button className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm">
                            Cancel Subscription
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h4 className="font-medium text-gray-900 mb-2">No Active Subscription</h4>
                        <p className="text-gray-600 mb-4">Subscribe to access multiple premium agents at a discounted price</p>
                        <button 
                          onClick={() => setActiveTab('subscriptions')}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          View Subscription Plans
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Individual Purchases */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Your Purchased Agents</h3>
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <div className="text-center py-8">
                        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h4 className="font-medium text-gray-700 mb-2">No purchases yet</h4>
                        <p className="text-gray-500 mb-6">Start browsing the marketplace to find amazing AI agents!</p>
                        <button
                          onClick={() => setActiveTab('browse')}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Browse Marketplace
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Sign in to view your purchases</h3>
                  <p className="text-gray-600 mb-6">You need to be signed in to view your purchased agents and subscriptions</p>
                  <button
                    onClick={() => window.location.href = '/login'}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Sign In
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'sell' && (
            <div className="p-6 max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Sell Your AI Agent</h3>
                <p className="text-gray-600">Turn your AI creation into a revenue stream</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 bg-green-50 rounded-xl">
                  <DollarSign className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">Earn Revenue</h4>
                  <p className="text-sm text-gray-600">Keep 65-80% of sales revenue with our competitive creator rates</p>
                </div>
                
                <div className="text-center p-6 bg-blue-50 rounded-xl">
                  <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">Reach Customers</h4>
                  <p className="text-sm text-gray-600">Access our growing community of AI enthusiasts and businesses</p>
                </div>
                
                <div className="text-center p-6 bg-purple-50 rounded-xl">
                  <Zap className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                  <h4 className="font-semibold text-gray-900 mb-2">Easy Setup</h4>
                  <p className="text-sm text-gray-600">List your agent in minutes with our streamlined publishing process</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Publishing Requirements</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Working AI agent with clear documentation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Demo or preview functionality</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Proper licensing and usage rights</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm">Quality assurance review</span>
                  </div>
                </div>
                
                <div className="mt-6 flex gap-4">
                  <button className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium">
                    Start Publishing
                  </button>
                  <button className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    Learn More
                  </button>
                </div>
              </div>

              <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Pricing Guidelines
                </h4>
                <div className="text-yellow-800 text-sm space-y-2">
                  <p><strong>Maximum Price:</strong> Individual agents can be priced up to $20 maximum</p>
                  <p><strong>Subscription Eligibility:</strong> Mark your agent as "subscription eligible" to include it in subscription plans</p>
                  <p><strong>Revenue Share:</strong> Creators receive 70% of individual sales and a portion of subscription revenue</p>
                  <p className="text-xs mt-3">Note: All prices are in USD. Prices are subject to review by our team.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && checkoutItem && (
        <StripeCheckout
          item={checkoutItem}
          onClose={() => {
            setShowCheckout(false);
            setCheckoutItem(null);
          }}
          onSuccess={() => {
            setShowCheckout(false);
            setCheckoutItem(null);
            // Refresh user subscription if needed
            if (checkoutItem.type === 'subscription') {
              fetchUserSubscription();
            }
          }}
        />
      )}
    </div>
  );
}